import React, { useContext, useState } from 'react';
import AddButton from './AddButton';
import FormWrapper from './FormWrapper';
import AllowedEmailTextField from '../AllowedEmailTextField';
import { IconButton } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import Space from '@/components/Space';
import SchoolContext from '../../../SchoolContext';
import { type AllowedEmailWithId } from '../../useAllowedEmailsState';
import { type AllowedEmail } from '@/lib/types';

type AddAllowedEmailProps = {
  allowedEmails: AllowedEmailWithId[];
  onAdd: (allowedEmail: AllowedEmail) => void;
};

const AddAllowedEmail = ({ allowedEmails, onAdd }: AddAllowedEmailProps) => {
  const [loading] = useContext(SchoolContext);

  const [formOpen, setFormOpen] = useState(false);
  const [newAllowedEmailPrefix, setNewAllowedEmailPrefix] = useState('');
  const [newAllowedEmailDomain, setNewAllowedEmailDomain] = useState('');
  // TODO better error message to account for possible duplication in prefix too
  const [showEmailAlreadyAddedError, setShowEmailAlreadyAddedError] =
    useState(false);

  if (!formOpen) {
    return <AddButton onClick={() => setFormOpen(true)} disabled={loading} />;
  }

  const saveNewAllowedEmail = (e: React.SubmitEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const alreadyAddedAllowedEmails = allowedEmails.map(({ allowedEmail }) =>
      JSON.stringify(allowedEmail),
    );

    const newAllowedEmailAsString = JSON.stringify([
      newAllowedEmailPrefix,
      newAllowedEmailDomain,
    ]);

    if (alreadyAddedAllowedEmails.includes(newAllowedEmailAsString)) {
      setShowEmailAlreadyAddedError(true);
      return;
    }

    onAdd([newAllowedEmailPrefix, newAllowedEmailDomain]);

    setFormOpen(false);
    setNewAllowedEmailPrefix('');
    setNewAllowedEmailDomain('');
    setShowEmailAlreadyAddedError(false);
  };

  return (
    <FormWrapper onSubmit={saveNewAllowedEmail}>
      <AllowedEmailTextField
        value={newAllowedEmailPrefix}
        onChange={e => setNewAllowedEmailPrefix(e.target.value.trim())}
        error={showEmailAlreadyAddedError}
        asPrefixField
      />
      <Space width={8} />
      <AllowedEmailTextField
        value={newAllowedEmailDomain}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setNewAllowedEmailDomain(e.target.value.trim())
        }
        error={showEmailAlreadyAddedError}
        helperText={
          showEmailAlreadyAddedError && (
            <FormattedMessage id="platform_settings.login_settings.add_allowed_email.email_already_added_error" />
          )
        }
        autoFocus
      />
      <IconButton type="submit" disabled={!newAllowedEmailDomain}>
        <CheckIcon />
      </IconButton>
    </FormWrapper>
  );
};

export default AddAllowedEmail;
